/**
 * Tab-visibility utilities.
 *
 * Two surfaces are exported:
 *
 * 1. `subscribeToTabVisibility(onChange)` — a non-hook utility callable at
 *    module load. It registers a single `document.addEventListener(
 *    'visibilitychange', …)` and invokes `onChange(isVisible)` whenever the
 *    document visibility flips. Returns an unsubscribe handle.
 *
 * 2. `useTabVisibility(onChange)` — a React-hook wrapper around the utility.
 *    Captures `onChange` in a ref so consumers can pass an inline closure
 *    without re-registering on every render (mirrors the pattern in
 *    `useInternetConnectivity`).
 *
 * Conceptual source: Flutter `chat_screen.dart:131-153` —
 * `didChangeAppLifecycleState` only fires on `resumed` / `paused`. The
 * existing two `document.visibilitychange` call-sites in the web project
 * (`videoManagementSlice.ts` and `RouteStack.tsx`) are migrated to this
 * single surface; new consumers (e.g. `useChat`) consume the hook variant.
 *
 * `beforeunload` is handled by a sibling surface. The non-hook utility
 * `subscribeToBeforeUnload` lives in `@/infrastructure/beforeUnload` (so the
 * domain layer can import it without crossing the inward-only dependency
 * arrow); the presentation-only `useBeforeUnload` React-hook wrapper below
 * delegates to it, mirroring `useTabVisibility`.
 *
 * SSR-defensive: if `document` is undefined, `subscribeToTabVisibility`
 * returns a no-op unsubscribe and skips listener registration.
 */
import { useEffect, useRef } from 'react';
import { subscribeToBeforeUnload } from '@/infrastructure/beforeUnload';

/**
 * Subscribe to tab-visibility changes outside of React.
 *
 * @param onChange - Invoked with the current visibility (`true` when the tab
 * is visible, `false` when it is hidden). Not called on subscription — call
 * `document.visibilityState === 'visible'` synchronously at the call-site if
 * an initial value is needed.
 * @returns An unsubscribe function that removes the underlying event
 * listener.
 */
export function subscribeToTabVisibility(
  onChange: (isVisible: boolean) => void,
): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }

  const handleVisibilityChange = () => {
    onChange(document.visibilityState === 'visible');
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

/**
 * React-hook wrapper around {@link subscribeToTabVisibility}.
 *
 * The callback is captured in a ref so consumers may pass an inline closure
 * without forcing the subscription to be torn down / re-registered on every
 * render. The underlying subscription is created once on mount and cleaned
 * up on unmount.
 *
 * Conditional / gated consumers: callers that need to no-op the callback
 * under some condition should perform the gate inside the callback itself
 * (e.g. `useTabVisibility((isVisible) => { if (!enabled) return; … })`).
 * The hook intentionally does not expose an `enabled` flag — keeping the
 * API minimal and uniform across consumers.
 *
 * @param onChange - Invoked with the current visibility (`true` for visible,
 * `false` for hidden) whenever the document visibility flips.
 */
export function useTabVisibility(
  onChange: (isVisible: boolean) => void,
): void {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const unsubscribe = subscribeToTabVisibility((isVisible) => {
      onChangeRef.current(isVisible);
    });
    return unsubscribe;
  }, []);
}

/**
 * React-hook wrapper around {@link subscribeToBeforeUnload} (imported from
 * `@/infrastructure/beforeUnload`).
 *
 * The callback is captured in a ref so consumers may pass an inline closure
 * without forcing the subscription to be torn down / re-registered on every
 * render. The underlying subscription is created once on mount and cleaned
 * up on unmount.
 *
 * @param onUnload - Invoked when the page is about to unload (tab closing,
 * reload, navigation away).
 */
export function useBeforeUnload(onUnload: () => void): void {
  const onUnloadRef = useRef(onUnload);
  onUnloadRef.current = onUnload;

  useEffect(() => {
    const unsubscribe = subscribeToBeforeUnload(() => {
      onUnloadRef.current();
    });
    return unsubscribe;
  }, []);
}
